import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFirefox } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { defaultLivePostCover, defaultLivePreCover } from '../../util/config';
import { isAllowAutoPlay, joinClassNames, toHHMMSS } from '../../util/util';
import { getHighQCoverFromRenditions, getHighQThumbnail, getHighQThumbnailFromResource } from '../../util/media-data-util';
import { NumberHelper } from '../../util/number-helper';
import { DateTimeHelper } from '../../util/date-time-helper';
import { setIsPlaying, setRemovePoster } from '../../actions/playerActions';
import * as QPlayerSVGElement from '../QPlayerSVGElement';

import './Poster.scss';

const DEFAULT_COLOR = '#126cfc';
const DEFAULT_PLAY_BUTTON_WIDTH = 120;
const DEFAULT_PLAY_BUTTON_X = 50;
const DEFAULT_PLAY_BUTTON_Y = 50;

interface PosterProps {
	playerId: string;
	queryParams: any;
	settings: any;
	isEditorPlayer: any;
	isMobile: boolean;
	playerConfigs: any;
}

const Poster = ({ playerId, queryParams, settings, isEditorPlayer, isMobile, playerConfigs }: PosterProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { mediasData, mediaType, isMediaLive, showCountdownModule, currentMediaId} = useSelector(
		(state) => (state as any).configurationReducer
	);
	const { isPosterRemoved, isPlaying, playerReady, isLiveStarted, isLiveStopped, currentPosition, duration } = useSelector(
		(state) => (state as any).playerReducer
	);

	const [isNoThumbnail, setNoThumbnail] = useState(false);
	const [isPlayButtonHover, setPlayButtonHover] = useState(false);
	const [isPlayButtonFocus, setPlayButtonFocus] = useState(false);
	const [thumbnailDimension, setThumbnailDimension] = useState({ width: 0, height: 0 });

	const thumbnailRef = useRef<HTMLDivElement>(null);

	const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
	const isAllowToAutoPlay = useMemo(() => {
		return isEditorPlayer || isAllowAutoPlay({ queryParams, settings });
	}, [isEditorPlayer, queryParams, settings]);

	const playBtnConfig = useMemo(() => {
		const color = playerConfigs?.playerStyling?.primaryColor ?? DEFAULT_COLOR;
		const icon = (playerConfigs?.playCover?.customPlayButtonIcon ? playerConfigs?.playCover?.customPlayButtonUrl : '') ?? '';
		const btnWidth = playerConfigs?.playCover?.playButtonSizeWidth ?? DEFAULT_PLAY_BUTTON_WIDTH;
		const btnHeight = icon ? null : btnWidth * 0.6;

		const xByPercentage = playerConfigs?.playCover?.playButtonPositionX ?? DEFAULT_PLAY_BUTTON_X;
		const yByPercentage = playerConfigs?.playCover?.playButtonPositionY ?? DEFAULT_PLAY_BUTTON_Y;

		const isShowVideoLength = icon || mediaType === 'scenario' || isMediaLive ? false : playerConfigs?.playCover?.showVideoLength ?? false;

		return { color, icon, width: btnWidth, height: btnHeight, x: xByPercentage, y: yByPercentage, isShowVideoLength };
	}, [playerConfigs, mediaType, isMediaLive, thumbnailDimension]);

	const { thumbnailUrl, preCoverUrl, postCoverUrl } = useMemo(() => {
		if (!currentMedia || !currentMedia?.mediaDetails) {
			setNoThumbnail(false)
			return {};
		}

		if ((currentMedia.playlistAsset?.resources ?? []).length > 0) {
			const { resources } = currentMedia.playlistAsset;
			const thumbnailUrl = getHighQThumbnailFromResource(resources)
			setNoThumbnail(!thumbnailUrl)
			return { thumbnailUrl };
		}

		const { posters, images } = currentMedia;
		const thumbnailUrl = getHighQThumbnail({ posters, images });
		setNoThumbnail(!thumbnailUrl)
		return {
			thumbnailUrl: thumbnailUrl,
			preCoverUrl: getHighQCoverFromRenditions(images, "precover") || defaultLivePreCover,
			postCoverUrl: getHighQCoverFromRenditions(images, "postcover") || defaultLivePostCover,
		};
	}, [currentMedia]);

	const handlePlay = () => {
		setTimeout(() => {
			const qplayerAppElem = document.querySelector('div#main-container') as any;
			qplayerAppElem?.focus();
			dispatch(setRemovePoster());
		});

		playerId && (window as any).GoBrain.widgets(playerId)?.play?.();

		if (isFirefox) {
			// Work around for need to click twice to play on Firefox
			setTimeout(() => {
				playerId && (window as any).GoBrain.widgets(playerId)?.play?.();
				dispatch(setRemovePoster());
			}, 100);
		}
	};

	const showPostCover = !isLiveStopped || typeof isLiveStopped === "boolean" ? isLiveStopped : (isPlaying === undefined || (isPlaying && mediaType === "scenario")) && Math.ceil(NumberHelper.precision3(currentPosition)) === Math.ceil(NumberHelper.precision3(isLiveStopped));

	useEffect(() => {
		if (isMediaLive && showPostCover) {
			dispatch(setIsPlaying(undefined));
		}
	}, [dispatch, isMediaLive, showPostCover]);

	useEffect(() => {
		thumbnailRef.current &&
			new ResizeObserver((changes) => {
				changes.forEach((change) => {
					const { contentRect } = change;

					if (contentRect?.width && contentRect?.height) {
						setThumbnailDimension({ width: contentRect?.width, height: contentRect?.height });
					}
				});
			}).observe(thumbnailRef.current);
	}, []);

	const renderPlayButton = () =>
		!playerReady ? null : playBtnConfig.icon ? (
			<div
				className="play-button"
				tabIndex={0}
				role="button"
				aria-label={`${t('PLAY_WITH_DURATION_ARIA_LABEL')} ${DateTimeHelper.formatHumanizeDuration(toHHMMSS(duration), t)}`}
				style={{
					top: `${playBtnConfig.y}%`,
					left: `${playBtnConfig.x}%`,
					width: `${playBtnConfig.width}px`,
					height: playBtnConfig.height ?? 'auto',
				}}
				onMouseEnter={isMobile ? undefined : () => setPlayButtonHover(true)}
				onMouseLeave={isMobile ? undefined : () => setPlayButtonHover(false)}
				onFocus={isMobile ? undefined : () => setPlayButtonFocus(true)}
				onBlur={isMobile ? undefined : () => setPlayButtonFocus(false)}
				onClick={handlePlay}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handlePlay();
					}
				}}
			>
				<div className="play-icon full-width" aria-hidden={true}>
					<img src={playBtnConfig.icon} alt="play-icon" />
					<div
						className="custom-icon-overlay"
						style={{
							backgroundColor: isPlayButtonHover || isPlayButtonFocus ? playBtnConfig.color : 'transparent',
						}}
					/>
				</div>
			</div>
		) : (
			<div
				className={`play-button${isPlayButtonHover ? ' hovered' : ''}`}
				tabIndex={0}
				role="button"
				aria-label={`${t('PLAY_WITH_DURATION_ARIA_LABEL')} ${DateTimeHelper.formatHumanizeDuration(toHHMMSS(duration), t)}`}
				style={{
					backgroundColor: isPlayButtonHover || isPlayButtonFocus ? playBtnConfig.color : '#000000cc',
					top: `${playBtnConfig.y}%`,
					left: `${playBtnConfig.x}%`,
					width: `${playBtnConfig.width}px`,
					height: `${playBtnConfig.height}px`,
				}}
				onMouseEnter={isMobile ? undefined : () => setPlayButtonHover(true)}
				onMouseLeave={isMobile ? undefined : () => setPlayButtonHover(false)}
				onFocus={isMobile ? undefined : () => setPlayButtonFocus(true)}
				onBlur={isMobile ? undefined : () => setPlayButtonFocus(false)}
				onClick={handlePlay}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handlePlay();
					}
				}}
			>
				<div className="play-icon" aria-hidden={true}>
					<QPlayerSVGElement.Play />
				</div>

				{playBtnConfig.isShowVideoLength && !!(duration || currentMedia?.duration) && (
					<div aria-hidden={true} className={`video-duration`}>
						{toHHMMSS(duration || currentMedia?.duration)}
					</div>
				)}
			</div>
		);

	if (showCountdownModule || isEditorPlayer) {
		return null;
	}

	// Live stop, show post-cover if there's one
	if (isMediaLive && showPostCover && postCoverUrl) {
		return (
			<div
				className={`qplayer-poster`}
				ref={thumbnailRef}
				style={postCoverUrl ? { backgroundImage: `url('${postCoverUrl}')`, } : undefined}
			/>
		);
	}

	// Live hasn't started yet, show pre-cover if there's one
	if (isMediaLive && !isLiveStarted && preCoverUrl && !isLiveStopped) {
		return (
			<div
				className={`qplayer-poster`}
				ref={thumbnailRef}
				style={preCoverUrl ? { backgroundImage: `url('${preCoverUrl}')`, } : undefined}
			/>
		);
	}

	if (isAllowToAutoPlay) {
		return null;
	}

	// Live started, show thumbnail if it isn't autoplay
	return (
		(!!isLiveStarted || !isMediaLive) &&
		!isLiveStopped &&
		!isPosterRemoved &&
		!isPlaying ? (
			<div
				className={joinClassNames(`qplayer-poster`, isNoThumbnail ? 'qplayer-poster--no-thumbnail' : '')}
				ref={thumbnailRef}
				style={thumbnailUrl ? { backgroundImage: `url('${thumbnailUrl}')`, } : undefined}
			>
				{renderPlayButton()}
			</div>
		) : null
	);
};

export default Poster;
